import { digger } from '@cbre/digger';
import { areaUomAbbrev, concatValuesBy, defaultTxt, EMPTY_STRING, formatNumber } from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { DOLLAR_SIGN, getUOMValue, PERCENT_SIGN } from '../us.common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  321: {
    // Building Name
    displayFn: displayFnWrapper,
  },
  322: {
    // 322 Address
    displayFn: (record: ReportRecord, _, street, subMarket, district) => {
      const values = [subMarket, district].map(arg => {
        const mappedValue = mapAndFormatDynamicValue(record, arg) as string;
        const value = mappedValue == null ? EMPTY_STRING : mappedValue;
        return value === defaultTxt ? EMPTY_STRING : value;
      });
      const topVal = mapAndFormatDynamicValue(record, street);
      const bottomVal = values.every(value => value == EMPTY_STRING)
        ? defaultTxt
        : values.some(value => value == EMPTY_STRING)
          ? values.join('')
          : values.join('/');
      return concatValuesBy(' \n', topVal, bottomVal);
    },
  },
  323: {
    // 323 Size (SF)
    displayFn: displayFnWrapper,
  },
  324: {
    // 324 Price Per SF Cap Rate
    displayFn: (record: ReportRecord, _, pricePerSF, capRate) => {
      const [price] = mapAndFormatDynamicValue(record, pricePerSF, [], false);
      const [rate] = mapAndFormatDynamicValue(record, capRate);
      const values = concatValuesBy(
        '\n ',
        price == null || price === defaultTxt ? defaultTxt : DOLLAR_SIGN + price,
        rate == null || rate === defaultTxt ? defaultTxt : rate + PERCENT_SIGN
      );
      return values;
    },
  },
  325: {
    // 325 Sale Price
    displayFn: (record: ReportRecord, _, salePrice) => {
      const [price] = mapAndFormatDynamicValue(record, salePrice, [], false);
      return price == null || price === defaultTxt ? defaultTxt : DOLLAR_SIGN + price;
    },
  },
  326: {
    // 326 Property Type Class
    displayFn: (record: ReportRecord, _, propertyType, propertyClass) => {
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, propertyType),
        mapAndFormatDynamicValue(record, propertyClass)
      );
      return values;
    },
  },
  327: {
    // 327 Buyer
    displayFn: (record: ReportRecord, _, buyers, buyerReps) => {
      const buyer = mapAndFormatDynamicValue(record, buyers);
      const buyerRep = mapAndFormatDynamicValue(record, buyerReps, null, null, null, ' ');
      const values =
        buyer !== defaultTxt ? concatValuesBy(' | ', getCompaniesFromList(buyer), getCompaniesFromList(buyerRep)) : ' ';
      return values === defaultTxt ? ' ' : values;
    },
  },
  328: {
    // 328 Seller
    displayFn: (record: ReportRecord, _, sellers, sellerReps) => {
      const seller = mapAndFormatDynamicValue(record, sellers);
      const sellerRep = mapAndFormatDynamicValue(record, sellerReps, null, null, null, ' ');
      const values =
        seller !== defaultTxt
          ? concatValuesBy(' | ', getCompaniesFromList(seller), getCompaniesFromList(sellerRep))
          : ' ';
      return values === defaultTxt ? ' ' : values;
    },
  },
  330: {
    // 330 Total Size (SF)
    displayFn: (record: ReportRecord[], _, space) => {
      let totalSize = 0;
      let uom = null;
      record.forEach(rec => {
        const size = digger(rec.data, space.field);
        const uomValue = space.uomArgument ? digger(rec.data, space.uomArgument.field) : null;
        uom = uomValue ? getUOMValue(uom, uomValue) : null;
        if (size) {
          totalSize += parseInt(size);
        }
      });
      if (space.uomArgument) {
        return space.uomArgument.lovValues.pipe(
          map((currencyLOV: LovValues[]) => {
            if (uom && currencyLOV) {
              const lovValue = currencyLOV.find(a => a.key === uom);
              if (lovValue) {
                uom = areaUomAbbrev(lovValue.value);
              }
            }
            return formatNumber(totalSize) + ' ' + (uom || '');
          })
        );
      } else {
        return formatNumber(totalSize) + ' SF';
      }
    },
  },
};

function getCompaniesFromList(list: string | string[]) {
  if (Array.isArray(list)) {
    const companies = list.filter((item, index, array) => array.indexOf(item) === index);
    return companies;
  }
  return list;
}
